import { Button, DataTable, FlexLayout, Icon } from '@deity-io/portal-ui';
import { BaseHelmet, ConfirmationModal, CreateMappingModal, MappingStatusBadge } from 'components';
import { APOLLO_BASE_OPTIONS, ENTITY_PATH, ENTITY_PATH_ID, toLocalDateString } from 'contracts/constants';
import { DEFAULT_PAGINATION_SIZE } from 'contracts/constants/page-title';
import { EntityWrapper } from 'layouts';
import type { FC } from 'react';
import { useEffect, useState } from 'react';
import { useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import {
  GetMappingsByPackageVersionDocument,
  useDeleteMappingMutation,
  useGetMappingsByPackageVersionLazyQuery
} from 'services/graphql/generated/hooks';
import { getParams } from 'utils';
import { v4 } from 'uuid';

interface MappingsProps {
  isEditable: boolean;
}
const Mappings: FC<MappingsProps> = ({ isEditable }) => {
  const { url } = useRouteMatch();
  const history = useHistory();
  const [paging, setPaging] = useState({ page: 1, pageSize: DEFAULT_PAGINATION_SIZE });
  const [modalOpen, setModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [mappingToDelete, setMappingToDelete] = useState<any>();
  const { pathname } = useLocation();
  const params: any = getParams(pathname, [ENTITY_PATH_ID, ENTITY_PATH]);
  const [getAllMappings, { loading, data: mappings, fetchMore }] =
    useGetMappingsByPackageVersionLazyQuery(APOLLO_BASE_OPTIONS);
  const [newMappingKey, setNewMappingKey] = useState(v4());

  const [deleteMapping] = useDeleteMappingMutation({
    refetchQueries: [
      {
        query: GetMappingsByPackageVersionDocument,
        variables: {
          packageVersionUuid: params?.versionUuid,
          offset: 0,
          limit: mappings?.getMappingsByPackageVersion?.data.length || 25
        }
      }
    ]
  });

  const totalItems = mappings?.getMappingsByPackageVersion?.totalItems;
  const data = mappings?.getMappingsByPackageVersion?.data;

  useEffect(() => {
    if (params?.versionUuid) {
      getAllMappings({
        variables: {
          offset: (paging.page - 1) * paging.pageSize,
          limit: paging.pageSize,
          packageVersionUuid: params.versionUuid
        }
      });
    }
  }, [pathname, paging]);

  // eslint-disable-next-line react/no-unstable-nested-components
  const NewEndpointButton = () => (
    <Button variant="outlined" disabled={modalOpen} onClick={() => setModalOpen(true)} startIcon={<Icon icon="add" />}>
      New Mapping
    </Button>
  );

  return (
    <>
      <BaseHelmet page="Mappings" />

      <EntityWrapper title="Mappings" button={<NewEndpointButton />}>
        <DataTable
          data={data || []}
          columns={[
            {
              key: 'name',
              name: 'MAPPING',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: ({ value }) => <b>{value}</b>,
              width: 40
            },
            {
              key: 'name',
              name: 'STATUS',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: ({ value }) => (
                <MappingStatusBadge packageVersionUuid={params?.versionUuid} schema={value.schema} />
              )
            },
            {
              key: 'modifiedAt',
              name: 'LAST MODIFIED',
              formatter: ({ value }) => toLocalDateString(value!)
            },
            {
              key: 'modifiedBy',
              name: 'LAST MODIFIED BY',
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: () => <div>John Smith</div>
            },
            {
              key: 'uuid',
              name: 'ACTIONS',
              isActionCell: true,
              // eslint-disable-next-line react/no-unstable-nested-components
              formatter: ({ value }) => (
                <FlexLayout>
                  <Button endIcon={<Icon icon="visibility" />} onClick={() => history.push(`${url}/${value}`)} />

                  {isEditable && (
                    <Button
                      endIcon={<Icon icon="delete" color="error" />}
                      onClick={() => {
                        setMappingToDelete(value);
                        setIsDeleteModalOpen(true);
                      }}
                    />
                  )}
                </FlexLayout>
              )
            }
          ]}
          pageSize={paging.pageSize}
          onPageSizeChange={size => {
            setPaging({ ...paging, pageSize: size });
          }}
          totalResults={totalItems || 0}
          page={paging.page}
          showPagination
          onPageChange={pageNr => {
            setPaging({ ...paging, page: pageNr });
          }}
          onRowClick={endpoint => history.push(`${url}/${endpoint.uuid}`)}
          noResultsMsg="No mappings available for this version"
          fullClickable
          isLoading={loading}
        />
      </EntityWrapper>

      <CreateMappingModal
        key={newMappingKey}
        isOpen={modalOpen}
        setOpenState={state => setModalOpen(state)}
        versionUuid={params?.versionUuid}
        onMappingCreated={() => {
          setModalOpen(false);
          setNewMappingKey(v4());
          setPaging({ ...paging, page: 1 });
        }}
      />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        entityType="mapping"
        entityUuid={mappingToDelete}
        onConfirm={uuid => deleteMapping({ variables: { packageVersionUuid: params.versionUuid, uuid: uuid! } })}
      />
    </>
  );
};

export default Mappings;
